import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { postData } from "../../services/apiService";
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';

const LoginPage = ({ onLogin, setIsAuthenticated, setProfile }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [shake, setShake] = useState(false);
  const [bubbles, setBubbles] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    const generateBubbles = () => {
      const newBubbles = Array.from({ length: 15 }, (_, index) => ({
        id: index,
        size: Math.random() * 60 + 20,
        left: Math.random() * 100,
        animationDuration: Math.random() * 20 + 10,
        animationDelay: Math.random() * 5,
        opacity: Math.random() * 0.3 + 0.1,
        blur: Math.random() * 10 + 5,
      }));
      setBubbles(newBubbles);
    };

    generateBubbles();
  }, []);

  const getErrorMessage = (error) => {
    if (error.response) {
      switch (error.response.status) {
        case 401:
          return "Invalid email or password. Please try again.";
        case 403:
          return "Access denied. Please contact administrator.";
        case 500:
          return "Server error. Please try again later.";
        default:
          return "Login failed. Please try again.";
      }
    } else if (error.request) {
      return "Network error. Please check your connection.";
    } else {
      return "An unexpected error occurred.";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.email || !formData.password) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setFormError("Please fill in all required fields.");
      return;
    }

    setIsLoading(true);

    try {
      const loginPayload = {
        username: "",
        password: formData.password,
        email: formData.email,
        projectId: "24",
        mobile: ""
      };

      const data = await postData("/Authentication/login", loginPayload);

      if (data && data.result && data.result.x_Request_ID) {
        const cookieOptions = {
          secure: process.env.NODE_ENV === 'production',
          sameSite: 'Strict',
          path: '/',
          expires: 1 // 1 day
        };

        // Set auth token
        Cookies.set('authToken', data.result.x_Request_ID, cookieOptions);

        // Set user data in cookies
        if (data.result.user) {
          const user = data.result.user;
          const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY || 'your-secret-key';

          const encryptValue = (value) => CryptoJS.AES.encrypt(value.toString(), encryptionKey).toString();

          // Get role from the roles array
          const userRole = user.roles && user.roles.length > 0 ? user.roles[0].RoleName : null;

          if (user.user_id) Cookies.set('u_id', encryptValue(user.user_id), cookieOptions);
          if (user.user_name) Cookies.set('u_nm', encryptValue(user.user_name), cookieOptions);
          if (user.inst_id != null) Cookies.set('i_id', encryptValue(user.inst_id), cookieOptions);
          if (user.inst_name) Cookies.set('i_nm', encryptValue(user.inst_name), cookieOptions);
          if (user.isFirstLogin !== undefined) Cookies.set('f_log', encryptValue(user.isFirstLogin), cookieOptions);

          // Store role from the roles array
          if (userRole) Cookies.set('from', encryptValue(userRole), cookieOptions);
          if (userRole) Cookies.set('r_id', encryptValue(userRole), cookieOptions);
        }

        // Check if login was successful - FIXED: Use isError instead of IsError
        if (data.isError === false) {
          if (setIsAuthenticated) setIsAuthenticated(true);
          if (setProfile && data.result.user) setProfile(data.result.user);
          if (onLogin) onLogin(data.result.user);

          // Smooth transition before navigation
          setTimeout(() => {
            navigate('/dashboard');
          }, 500);
        } else {
          setFormError(data.message || "Login failed. Please try again.");
        }
      } else {
        setFormError("Login failed. Invalid server response.");
      }
    } catch (err) {
      console.error("Login error:", err);
      setFormError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (formError) setFormError('');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="min-h-screen bg-blue-700 relative overflow-hidden">
      {/* Animated Bubbles */}
      <div className="absolute inset-0 overflow-hidden">
        {bubbles.map(bubble => (
          <div
            key={bubble.id}
            className="absolute rounded-full bg-gradient-to-br from-white/20 via-blue-200/15 to-purple-200/10 backdrop-blur-sm"
            style={{
              width: `${bubble.size}px`,
              height: `${bubble.size}px`,
              left: `${bubble.left}%`,
              top: '110%',
              opacity: bubble.opacity,
              filter: `blur(${bubble.blur}px)`,
              animation: `floatUp ${bubble.animationDuration}s ease-in-out ${bubble.animationDelay}s infinite`,
            }}
          />
        ))}
      </div>

      {/* Existing Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-blue-400/15 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"></div>
      </div>

      <div className="relative z-10 flex min-h-screen">
        {/* Left Side - Branding */}
        <div className="hidden lg:flex flex-1 flex-col items-center justify-center p-8 text-white text-center">
          <div className="space-y-4 max-w-lg">
            <div className="space-y-2">
              <div className="flex justify-center">
                <div className="w-48 h-48 rounded-2xl flex items-center justify-center transition-all duration-300 p-2">
                  <img
                    src="/EMS/Images/Logo.png"
                    alt="DeskApp Logo"
                    className="w-full h-full object-contain drop-shadow-md"
                  />
                </div>
              </div>

              <div className="space-y-6">
                <h1 className="text-5xl font-bold leading-tight">
                  Event Management
                  <span className="block bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent mt-4">
                    Made Simple
                  </span>
                </h1>

                <p className="text-blue-100 text-xl leading-relaxed font-light">
                  Streamline your event management process with our powerful platform.
                  Track payments, manage participants, and analyze performance effortlessly.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex-1 flex items-center justify-center p-1">
          <div className="w-full max-w-md">
            <div className={`bg-white backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl px-8 pb-6 space-y-4 transition-all duration-300 ${shake ? 'animate-shake' : ''}`}>

              {/* Mobile Header */}
              <div className="lg:hidden text-center space-y-4 pb-6">
                <div className="flex  items-center justify-center space-x-3 my-4">
                    <img
                      src="/EMS/Images/Logo.png"
                      alt="DeskApp Logo"
                      className="w-24 h-24object-contain drop-shadow-md"
                    />
             
                </div>
                <p className="text-blue-700 text-lg">Sign in to your account</p>
              </div>

              {/* Desktop Header */}
              <div className="hidden lg:block text-center">
                <div className="flex justify-center">
                  <div className="w-24 h-24 rounded-2xl flex items-center justify-center transition-all duration-300">
                    <img
                      src="/EMS/Images/Logo.png"
                      alt="DeskApp Logo"
                      className="w-full h-full object-contain drop-shadow-md"
                    />
                  </div>
                </div>
                <h2 className="text-xl font-bold bg-blue-800 text-transparent bg-clip-text">
                  Event Management System
                </h2>
                <p className="text-blue-900 text-lg">Sign in to your account</p>
              </div>

              {/* Error Message */}
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {formError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email Field */}
                <div className="space-y-3">
                  <label className="text-sm font-medium text-blue-700">Email Address</label>
                  <div className="relative group">
                    <div className="absolute inset-0 bg-blue-500 rounded-xl blur opacity-30 group-hover:opacity-40 transition duration-300" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      autoComplete='off'
                      className="relative w-full px-4 py-4 bg-white backdrop-blur-sm border border-blue-800 rounded-xl text-blue-900 placeholder-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400 transition-all duration-300"
                      placeholder="Enter your email"
                    />
                    <svg className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-200" fill="none" stroke="purple" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                  </div>
                </div>

                {/* Password Field */}
                <div className="space-y-3">
                  <label className="text-sm font-medium text-blue-700">Password</label>
                  <div className="relative group">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl blur opacity-30 group-hover:opacity-40 transition duration-300" />
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      autoComplete='off'
                      value={formData.password}
                      onChange={handleChange}
                      required
                      className="relative w-full px-4 py-4 bg-white backdrop-blur-sm border border-blue-800 rounded-xl text-blue-900 placeholder-blue-700 focus:outline-none focus:ring-2 focus:ring-purple-400/50 focus:border-purple-400 transition-all duration-300 pr-12"
                      placeholder="Enter your password"
                    />

                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-blue-200 hover:text-white transition-colors duration-200 focus:outline-none"
                    >
                      {showPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="purple" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="purple" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-end text-sm">
                  <button type="button" onClick={() => navigate('/ForgetPassword')} className="text-blue-700 hover:text-blue-800 transition-colors duration-200 font-medium">
                    Forgot password?
                  </button>
                </div>

                {/* Login Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-4 px-6 bg-blue-800 text-white font-semibold rounded-xl shadow-2xl hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/5 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  <span className="relative flex items-center justify-center space-x-3">
                    {isLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span className="font-medium">Signing In...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                        </svg>
                        <span className="font-medium">Sign In</span>
                      </>
                    )}
                  </span>
                </button>
              </form>

              {/* Footer */}
              <div className="text-center pt-6 border-t border-white/20">
                <div className="mt-4 pt-4 border-t border-blue-100/20">
                  <p className="text-blue-600 text-xs">
                    © {new Date().getFullYear()} Event Management System. All rights reserved psg software technologies.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;